import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createAdminDashboardNote,
  createAdminDashboardTodo,
  deleteAdminDashboardNote,
  getAdminDashboard,
  updateAdminDashboardTodo,
} from "../api/instateamAdminApi";
import { AppIcon } from "../components/InstateamMasterComponents";

const todayDateKey = () => new Date().toISOString().slice(0, 10);
const NOTE_TONES = ["amber", "green", "blue", "violet", "rose"];
const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

function DonutChart({ center, subtext, segments = [] }) {
  const activeSegments = segments.filter((segment) => Number(segment.value || 0) > 0);
  const total = activeSegments.reduce((sum, segment) => sum + Number(segment.value || 0), 0);
  let start = 0;
  const gradient = total
    ? activeSegments
        .map((segment) => {
          const size = (Number(segment.value || 0) / total) * 100;
          const part = `${segment.color} ${start}% ${start + size}%`;
          start += size;
          return part;
        })
        .join(", ")
    : "#e5e7eb 0 100%";

  return (
    <div className="admin-dashboard-donut-wrap">
      <div className="admin-dashboard-donut" style={{ background: `conic-gradient(${gradient})` }}>
        <div>
          <strong>{center}</strong>
          <span>{subtext}</span>
        </div>
      </div>
      <div className="admin-dashboard-donut-legend">
        {segments.map((segment) => (
          <div key={segment.label}>
            <i style={{ background: segment.color }} />
            <span>{segment.label}</span>
            <strong>{segment.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function Modal({ title, subtitle, onClose, children, onSubmit, submitLabel, saving }) {
  return (
    <div className="admin-dashboard-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        className="admin-dashboard-modal"
        onSubmit={onSubmit}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="admin-dashboard-modal-head">
          <div>
            <h3>{title}</h3>
            <p>{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <AppIcon name="close" />
          </button>
        </div>
        {children}
        <div className="admin-dashboard-modal-actions">
          <button type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" disabled={saving}>
            {saving ? "Saving..." : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDashboardPage({
  token,
  searchText = "",
  onNavigate = () => {},
  onNotify = () => {},
  onProtectedError = () => false,
}) {
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [dateFrom, setDateFrom] = useState(todayDateKey());
  const [dateTo, setDateTo] = useState(todayDateKey());
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [attendancePeriod, setAttendancePeriod] = useState("week");
  const [leavePeriod, setLeavePeriod] = useState("month");
  const [distributionDepartment, setDistributionDepartment] = useState("all");
  const [todoBucket, setTodoBucket] = useState("today");
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "",
    dueDate: todayDateKey(),
    dueTime: "",
    meta: "",
  });
  const [noteForm, setNoteForm] = useState({ title: "", details: "" });
  const [isSaving, setIsSaving] = useState(false);
  const [updatingTodoId, setUpdatingTodoId] = useState("");

  const fetchDashboard = useCallback(async ({ silent = false } = {}) => {
    if (!token) return;
    try {
      if (!silent) setIsLoading(true);
      const payload = await getAdminDashboard(token, {
        dateFrom,
        dateTo,
        attendancePeriod,
        leavePeriod,
      });
      setDashboard(payload);
      setLoadError("");
    } catch (error) {
      setLoadError(error.message || "Unable to load dashboard data right now.");
      if (!onProtectedError(error)) {
        onNotify({ type: "error", text: error.message });
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [attendancePeriod, dateFrom, dateTo, leavePeriod, onNotify, onProtectedError, token]);

  useEffect(() => {
    fetchDashboard();
    const intervalId = window.setInterval(() => fetchDashboard({ silent: true }), 60000);
    return () => window.clearInterval(intervalId);
  }, [fetchDashboard]);

  const filteredTodos = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return (dashboard?.todos || []).filter((todo) => {
      const matchesBucket = todo.bucket === todoBucket;
      if (!query) return matchesBucket;
      return matchesBucket && `${todo.title} ${todo.meta}`.toLowerCase().includes(query);
    });
  }, [dashboard?.todos, searchText, todoBucket]);

  const notes = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return dashboard?.notes || [];
    return (dashboard?.notes || []).filter((note) =>
      `${note.title} ${note.details}`.toLowerCase().includes(query)
    );
  }, [dashboard?.notes, searchText]);

  async function handleCreateTask(event) {
    event.preventDefault();
    if (!taskForm.title.trim() || !taskForm.dueDate || !taskForm.dueTime) {
      onNotify({ type: "warning", text: "Task name, deadline date, and time are required." });
      return;
    }
    try {
      setIsSaving(true);
      await createAdminDashboardTodo(token, taskForm);
      setTaskForm({ title: "", dueDate: todayDateKey(), dueTime: "", meta: "" });
      setIsTaskModalOpen(false);
      onNotify({ type: "success", text: "Task added successfully." });
      await fetchDashboard({ silent: true });
    } catch (error) {
      onNotify({ type: "error", text: error.message });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleTodo(todo) {
    if (!todo?._id || updatingTodoId) return;
    try {
      setUpdatingTodoId(todo._id);
      await updateAdminDashboardTodo(token, todo._id, { completed: !todo.completed });
      await fetchDashboard({ silent: true });
    } catch (error) {
      onNotify({ type: "error", text: error.message });
    } finally {
      setUpdatingTodoId("");
    }
  }

  async function handleCreateNote(event) {
    event.preventDefault();
    if (!noteForm.title.trim() || !noteForm.details.trim()) {
      onNotify({ type: "warning", text: "Note headline and details are required." });
      return;
    }
    try {
      setIsSaving(true);
      const tone = NOTE_TONES[(dashboard?.notes?.length || 0) % NOTE_TONES.length];
      await createAdminDashboardNote(token, { ...noteForm, tone });
      setNoteForm({ title: "", details: "" });
      setIsNoteModalOpen(false);
      onNotify({ type: "success", text: "Note added successfully." });
      await fetchDashboard({ silent: true });
    } catch (error) {
      onNotify({ type: "error", text: error.message });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteNote(noteId) {
    try {
      await deleteAdminDashboardNote(token, noteId);
      await fetchDashboard({ silent: true });
    } catch (error) {
      onNotify({ type: "error", text: error.message });
    }
  }

  if (isLoading && !dashboard) {
    return (
      <section className="admin-dashboard-page">
        <div className="admin-dashboard-loading-card">
          <div className="admin-dashboard-empty-icon">
            <AppIcon name="dashboard" />
          </div>
          <h2>Loading dashboard...</h2>
          <p>We are gathering the latest employees, attendance, payroll, leaves, and updates.</p>
        </div>
      </section>
    );
  }

  if (!dashboard) {
    return (
      <section className="admin-dashboard-page">
        <div className="admin-dashboard-loading-card">
          <div className="admin-dashboard-empty-icon">
            <AppIcon name="dashboard" />
          </div>
          <h2>Dashboard unavailable</h2>
          <p>{loadError || "We could not load the admin dashboard right now."}</p>
          <button type="button" className="primary-button" onClick={() => fetchDashboard()}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  const departmentDistribution = dashboard?.distributions?.departments || [];
  const visibleDepartmentDistribution =
    distributionDepartment === "all"
      ? departmentDistribution
      : departmentDistribution.filter((item) => item.label === distributionDepartment);
  const maxDept = Math.max(...visibleDepartmentDistribution.map((item) => item.value), 1);

  return (
    <section className="admin-dashboard-page">
      <div className="admin-dashboard-toolbar">
        <button type="button" className="admin-dashboard-icon-btn" onClick={() => onNavigate("notifications")}>
          <AppIcon name="bell" />
        </button>
        <button type="button" className="admin-dashboard-icon-btn" onClick={() => onNavigate("chat")}>
          <AppIcon name="mail" />
        </button>
        <div className="admin-dashboard-date-menu">
          <button
            type="button"
            className="admin-dashboard-date-btn"
            onClick={() => setIsDateRangeOpen((value) => !value)}
          >
            <AppIcon name="calendar" />
            {dashboard?.date?.rangeLabel || "Today"}
            <AppIcon name="chevronDown" />
          </button>
          {isDateRangeOpen ? (
            <div className="admin-dashboard-date-popover">
              <label>
                <span>Start Date</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(event) => {
                    const nextDate = event.target.value || todayDateKey();
                    setDateFrom(nextDate);
                    if (dateTo < nextDate) setDateTo(nextDate);
                  }}
                />
              </label>
              <label>
                <span>End Date</span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={(event) => setDateTo(event.target.value || dateFrom)}
                />
              </label>
              <button type="button" onClick={() => setIsDateRangeOpen(false)}>
                Apply
              </button>
            </div>
          ) : null}
        </div>
        <div className="admin-dashboard-add-menu">
          <button
            type="button"
            className="admin-dashboard-add-btn"
            onClick={() => setIsAddMenuOpen((value) => !value)}
          >
            <AppIcon name="plus" />
            Add New
            <AppIcon name="chevronDown" />
          </button>
          {isAddMenuOpen ? (
            <div className="admin-dashboard-add-dropdown">
              <button type="button" onClick={() => { setIsTaskModalOpen(true); setIsAddMenuOpen(false); }}>
                <AppIcon name="tasks" /> Add Task
              </button>
              <button type="button" onClick={() => { setIsNoteModalOpen(true); setIsAddMenuOpen(false); }}>
                <AppIcon name="documents" /> Add Note
              </button>
              <button type="button" onClick={() => onNavigate("add-employee")}>
                <AppIcon name="userPlus" /> Add Employee
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="admin-dashboard-metrics">
        {(dashboard?.metrics || []).map((metric) => (
          <article key={metric.key} className="admin-dashboard-metric">
            <div className={`admin-dashboard-metric-icon ${metric.tone}`}>
              <AppIcon name={metric.icon} />
            </div>
            <div>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <small>{metric.helper}</small>
            </div>
          </article>
        ))}
      </div>

      <div className="admin-dashboard-grid">
        <article className="admin-dashboard-card admin-dashboard-attendance">
          <div className="admin-dashboard-card-head">
            <h3>Attendance Overview</h3>
            <label className="admin-dashboard-inline-select">
              <select value={attendancePeriod} onChange={(event) => setAttendancePeriod(event.target.value)}>
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>
          </div>
          <DonutChart center={`${dashboard?.attendance?.score || 0}%`} subtext="Average Attendance" segments={dashboard?.attendance?.segments || []} />
          <div className="admin-dashboard-two-stats">
            <span>Total Working Days <strong>{dashboard?.attendance?.totalWorkingDays || 0}</strong></span>
            <span>Total Employees <strong>{dashboard?.attendance?.totalEmployees || 0}</strong></span>
          </div>
        </article>

        <article className="admin-dashboard-card admin-dashboard-leave">
          <div className="admin-dashboard-card-head">
            <h3>Leave Overview</h3>
            <label className="admin-dashboard-inline-select">
              <select value={leavePeriod} onChange={(event) => setLeavePeriod(event.target.value)}>
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>
          </div>
          <DonutChart center={dashboard?.leave?.total || 0} subtext="Total Leaves" segments={dashboard?.leave?.segments || []} />
          <div className="admin-dashboard-progress-row">
            <span>Leave Utilization</span>
            <i><em style={{ width: `${dashboard?.leave?.utilization || 0}%` }} /></i>
            <strong>{dashboard?.leave?.utilization || 0}%</strong>
          </div>
        </article>

        <article className="admin-dashboard-card admin-dashboard-payroll">
          <div className="admin-dashboard-card-head">
            <h3>Payroll Summary ({dashboard?.payroll?.monthKey || ""})</h3>
            <button type="button" onClick={() => onNavigate("payroll")}><AppIcon name="more" /></button>
          </div>
          <div className="admin-dashboard-payroll-grid">
            <div className="admin-dashboard-payroll-cost">
              <div className="admin-dashboard-metric-icon green"><AppIcon name="payroll" /></div>
              <span>Total Payroll Cost</span>
              <strong>{dashboard?.payroll?.totalCostLabel || "0"}</strong>
              <small>Live from payroll records</small>
            </div>
            <div className="admin-dashboard-payroll-mini"><AppIcon name="employees" /> Paid Employees <strong>{dashboard?.payroll?.paidEmployees || 0}</strong></div>
            <div className="admin-dashboard-payroll-mini"><AppIcon name="userMinus" /> Pending Payments <strong>{dashboard?.payroll?.pendingPayments || 0}</strong></div>
            <div className="admin-dashboard-payroll-mini"><AppIcon name="documents" /> Total Deductions <strong>{dashboard?.payroll?.deductionsLabel || "0"}</strong></div>
          </div>
          <button type="button" className="admin-dashboard-link" onClick={() => onNavigate("payroll")}>
            View Payroll Details <AppIcon name="chevronRight" />
          </button>
        </article>

        <article className="admin-dashboard-card admin-dashboard-todos">
          <div className="admin-dashboard-card-head">
            <h3>To-Do List</h3>
            <button type="button" onClick={() => setIsTaskModalOpen(true)}>+ Add Task</button>
          </div>
          <div className="admin-dashboard-tabs">
            {["today", "tomorrow", "upcoming"].map((bucket) => (
              <button key={bucket} type="button" className={todoBucket === bucket ? "active" : ""} onClick={() => setTodoBucket(bucket)}>
                {bucket[0].toUpperCase() + bucket.slice(1)} ({(dashboard?.todos || []).filter((todo) => todo.bucket === bucket).length})
              </button>
            ))}
          </div>
          <div className="admin-dashboard-todo-list">
            {filteredTodos.length ? filteredTodos.map((todo) => (
              <label key={todo._id} className={`admin-dashboard-todo-item ${todo.completed ? "completed" : ""}`}>
                <input type="checkbox" checked={todo.completed} disabled={updatingTodoId === todo._id} onChange={() => handleToggleTodo(todo)} />
                <span>{todo.title}</span>
                {todo.meta ? <em>{todo.meta}</em> : null}
                <small>{todo.dueTime}</small>
              </label>
            )) : <p className="admin-dashboard-muted">No tasks in this list.</p>}
          </div>
          <button type="button" className="admin-dashboard-link" onClick={() => onNavigate("tasks")}>View All Tasks <AppIcon name="chevronRight" /></button>
        </article>

        <article className="admin-dashboard-card admin-dashboard-notes">
          <div className="admin-dashboard-card-head">
            <h3>Notes</h3>
            <button type="button" onClick={() => setIsNoteModalOpen(true)}>+ Add Note</button>
          </div>
          <div className="admin-dashboard-note-list">
            {notes.length ? notes.map((note) => (
              <article key={note._id} className={`admin-dashboard-note ${note.tone}`}>
                <AppIcon name="documents" />
                <div>
                  <strong>{note.title}</strong>
                  <p>{note.details}</p>
                </div>
                <span>{note.timeLabel}</span>
                <button type="button" aria-label={`Delete ${note.title}`} onClick={() => handleDeleteNote(note._id)}>
                  <AppIcon name="trash" />
                </button>
              </article>
            )) : <p className="admin-dashboard-muted">No notes yet.</p>}
          </div>
        </article>

        <article className="admin-dashboard-card admin-dashboard-activities">
          <div className="admin-dashboard-card-head">
            <h3>Recent Activities</h3>
            <button type="button" onClick={() => onNavigate("notifications")}>View All</button>
          </div>
          <div className="admin-dashboard-activity-list">
            {(dashboard?.activities || []).map((activity) => (
              <button key={activity.key} type="button" className={`admin-dashboard-activity ${activity.tone}`} onClick={() => onNavigate("notifications")}>
                <AppIcon name={activity.icon} />
                <div>
                  <strong>{activity.title}</strong>
                  <span>{activity.subtitle}</span>
                </div>
                <small>{activity.timeLabel}</small>
              </button>
            ))}
          </div>
        </article>

        <article className="admin-dashboard-card admin-dashboard-distribution">
          <div className="admin-dashboard-card-head">
            <h3>Employee Distribution</h3>
            <label className="admin-dashboard-inline-select admin-dashboard-department-select">
              <select
                value={distributionDepartment}
                onChange={(event) => setDistributionDepartment(event.target.value)}
              >
                <option value="all">All Departments</option>
                {departmentDistribution.map((item) => (
                  <option key={item.label} value={item.label}>
                    {item.label}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>
          </div>
          <div className="admin-dashboard-bars">
            {visibleDepartmentDistribution.slice(0, 8).map((item) => (
              <div key={item.label}>
                <span>{item.value}</span>
                <i style={{ height: `${Math.max(12, (item.value / maxDept) * 100)}%` }} />
                <small>{item.label}</small>
              </div>
            ))}
            {!visibleDepartmentDistribution.length ? (
              <p className="admin-dashboard-muted">No employees found for this department.</p>
            ) : null}
          </div>
        </article>

        <article className="admin-dashboard-card admin-dashboard-ratio">
          <h3>Gender Ratio</h3>
          <DonutChart center="" subtext="" segments={(dashboard?.distributions?.gender || []).map((item, index) => ({ ...item, color: ["#2f7df6", "#ff4aa2", "#94a3b8"][index] || "#94a3b8" }))} />
        </article>

        <article className="admin-dashboard-card admin-dashboard-type">
          <h3>Employment Type</h3>
          <DonutChart center="" subtext="" segments={(dashboard?.distributions?.employmentType || []).map((item, index) => ({ ...item, color: ["#2f7df6", "#32c979", "#f6b719", "#8b5cf6"][index] || "#94a3b8" }))} />
        </article>

        <article className="admin-dashboard-card admin-dashboard-events">
          <div className="admin-dashboard-card-head">
            <h3>Upcoming Events</h3>
            <button type="button" onClick={() => onNavigate("announcements")}>View Calendar</button>
          </div>
          {(dashboard?.events || []).map((event) => (
            <button key={event._id} type="button" className="admin-dashboard-event" onClick={() => onNavigate("announcements")}>
              <span>{event.dateLabel}</span>
              <strong>{event.title}</strong>
              <small>{event.timeLabel}</small>
            </button>
          ))}
        </article>
      </div>

      <div className="admin-dashboard-bottom-strip">
        <button type="button" onClick={() => onNavigate("leaves")}><AppIcon name="employees" /> Pending Approvals <strong>{dashboard?.summaries?.pendingApprovals || 0}</strong></button>
        <button type="button" onClick={() => onNavigate("complaints")}><AppIcon name="complaints" /> Open Tickets <strong>{dashboard?.summaries?.openTickets || 0}</strong></button>
        <button type="button" onClick={() => onNavigate("documents")}><AppIcon name="documents" /> Documents Expiring <strong>{dashboard?.summaries?.documentsExpiring || 0}</strong></button>
        <button type="button" onClick={() => onNavigate("employees")}><AppIcon name="userPlus" /> Probation Employees <strong>{dashboard?.summaries?.probationEmployees || 0}</strong></button>
        <div>Tip: Keep your team engaged and productive.</div>
      </div>

      {isTaskModalOpen ? (
        <Modal title="Add Task" subtitle="Create an admin dashboard task." onClose={() => setIsTaskModalOpen(false)} onSubmit={handleCreateTask} submitLabel="Add Task" saving={isSaving}>
          <label><span>Task Name</span><input value={taskForm.title} onChange={(event) => setTaskForm((current) => ({ ...current, title: event.target.value }))} placeholder="Enter task name" autoFocus /></label>
          <label><span>Deadline Date</span><input type="date" value={taskForm.dueDate} onChange={(event) => setTaskForm((current) => ({ ...current, dueDate: event.target.value }))} /></label>
          <label><span>Deadline Time</span><input type="time" value={taskForm.dueTime} onChange={(event) => setTaskForm((current) => ({ ...current, dueTime: event.target.value }))} /></label>
          <label><span>Small Label</span><input value={taskForm.meta} onChange={(event) => setTaskForm((current) => ({ ...current, meta: event.target.value }))} placeholder="Example: 10 Requests" /></label>
        </Modal>
      ) : null}

      {isNoteModalOpen ? (
        <Modal title="Add Note" subtitle="Save a dashboard note." onClose={() => setIsNoteModalOpen(false)} onSubmit={handleCreateNote} submitLabel="Add Note" saving={isSaving}>
          <label><span>Headline</span><input value={noteForm.title} onChange={(event) => setNoteForm((current) => ({ ...current, title: event.target.value }))} placeholder="Enter note headline" autoFocus /></label>
          <label><span>Details</span><textarea value={noteForm.details} onChange={(event) => setNoteForm((current) => ({ ...current, details: event.target.value }))} placeholder="Write note details" rows={5} /></label>
        </Modal>
      ) : null}
    </section>
  );
}
